import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, AlertCircle } from "lucide-react";
import { useTenantStore } from "@/stores/useTenantStore";
import {
	fetchGa4Properties,
	fetchTenantConnectSettings,
	saveGa4Connection,
	GA4_ADMIN_URL,
	type Ga4Property,
} from "@/services/connectFlowService";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";

type Step = "intro" | "already_connected" | "picker" | "confirm" | "success";

const STEP_NUMBERS: Partial<Record<Step, number>> = {
	intro: 1,
	picker: 2,
	confirm: 3,
};

function getApiErrorMessage(error: unknown, fallback: string): string {
	return (
		(error as { response?: { data?: { detail?: string } } })?.response?.data
			?.detail ||
		(error as Error)?.message ||
		fallback
	);
}

/**
 * Feedforward grant-access affordance (doc 61 §C2): states WHERE the action happens
 * (Google's admin) and links there — labeled with destination + "(opens in a new tab)",
 * styled as a link, never a second CTA button (one primary action per step).
 */
const grantAccessLink = (
	<a
		href={GA4_ADMIN_URL}
		target="_blank"
		rel="noreferrer"
		className="text-coral hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
	>
		Open GA4 (opens in a new tab)
	</a>
);

export default function ConnectGA4Page() {
	const { currentTenant } = useTenantStore();
	const queryClient = useQueryClient();
	const tenantScope = currentTenant ?? "no-tenant";

	const [step, setStep] = useState<Step>("intro");
	const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
		null,
	);
	const [connecting, setConnecting] = useState(false);
	const [connectError, setConnectError] = useState<string | null>(null);

	// --- Already-connected guard (Fix 3) ---
	const { data: settings } = useQuery({
		queryKey: ["connect-ga4-settings", tenantScope],
		queryFn: () => fetchTenantConnectSettings(currentTenant!),
		enabled: !!currentTenant,
		staleTime: 30 * 1000,
	});

	useEffect(() => {
		if (settings?.ga4_property_id && step === "intro") {
			setStep("already_connected");
		}
	}, [settings, step]);

	// --- Discovery ---
	const { data: propsData, isLoading, isError, error, refetch } = useQuery({
		queryKey: ["connect-ga4-properties", tenantScope],
		queryFn: () => fetchGa4Properties(currentTenant!),
		enabled: step === "picker" && !!currentTenant,
		retry: false,
		staleTime: 0,
	});

	const properties = propsData?.properties ?? [];
	const discoveryError = propsData?.error;

	const selectedProperty = properties.find(
		(p) => p.property_id === selectedPropertyId,
	);

	const handleConnect = async () => {
		if (!selectedPropertyId || !currentTenant || connecting) return;
		setConnecting(true);
		setConnectError(null);
		try {
			await saveGa4Connection(currentTenant, {
				ga4_property_id: selectedPropertyId,
			});
			// C1.4: refresh the already-connected guard so it reflects the new
			// property immediately (settings query carries a 30s staleTime).
			queryClient.invalidateQueries({
				queryKey: ["connect-ga4-settings", tenantScope],
			});
			queryClient.invalidateQueries({ queryKey: ["ga4", tenantScope] });
			setStep("success");
		} catch (err) {
			setConnectError(
				getApiErrorMessage(err, "Connection failed — please try again."),
			);
		} finally {
			setConnecting(false);
		}
	};

	// --- No tenant selected ---
	if (!currentTenant) {
		return (
			<div className="mx-auto max-w-2xl space-y-6 p-6">
				<h1 className="text-dash-page font-semibold text-foreground">
					Connect GA4
				</h1>
				<IntegrationStatePanel
					state="empty"
					message="Select a workspace to continue."
				/>
			</div>
		);
	}

	const currentStepNum = STEP_NUMBERS[step];

	return (
		<div className="mx-auto max-w-2xl space-y-6 p-6">
			<div>
				<Link
					to="/dashboard"
					className="text-dash-context text-muted-foreground hover:text-foreground"
				>
					← Dashboard
				</Link>
				<h1 className="mt-2 text-dash-page font-semibold text-foreground">
					Connect GA4
				</h1>
			</div>

			<Card className="p-6">
				{currentStepNum && (
					<p className="mb-4 text-dash-context text-muted-foreground">
						Step {currentStepNum} of 3
					</p>
				)}

				{step === "intro" && (
					<div className="rounded-lg border border-dashed border-warm-border-strong bg-warm-bg px-8 py-9 text-center">
						<p className="text-dash-title font-semibold text-ink">
							See how visitors actually use the site.
						</p>
						<div className="mx-auto mt-2 max-w-[460px] text-dash-subtitle text-ink-2">
							<p>
								Connect GA4 to see page views, sessions, and engagement trends.{" "}
								<strong className="text-foreground">Before you continue:</strong>{" "}
								Grant access to the agency&rsquo;s Google account in GA4, then come
								back here.
							</p>
							<div className="mt-2 text-dash-context">{grantAccessLink}</div>
						</div>
						<div className="mt-5">
							<Button
								onClick={() => setStep("picker")}
								className="min-h-11 bg-coral text-primary-foreground hover:bg-coral-hover"
							>
								Continue
							</Button>
						</div>
						<p className="mt-3.5 text-dash-context text-ink-3">
							Setup usually takes about a minute. GA4 data is live once connected
							— no backfill.
						</p>
					</div>
				)}

				{step === "already_connected" && settings && (
					<div className="space-y-4">
						<div className="rounded-lg border border-[hsl(var(--status-success))]/20 bg-[hsl(var(--status-success-bg))]/10 p-4">
							<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
								Already connected
							</span>
							<p className="mt-1 text-sm text-foreground">
								Property {settings.ga4_property_id}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setSelectedPropertyId(null);
									setConnectError(null);
									setStep("picker");
								}}
							>
								Choose a different property
							</Button>
						</div>
					</div>
				)}

				{step === "picker" && (
					<div className="space-y-4">
						<h2 className="text-dash-title font-semibold text-foreground">
							Select your property
						</h2>

						{isLoading ? (
							<div className="space-y-2">
								{Array.from({ length: 3 }).map((_, i) => (
									<Skeleton key={i} className="h-14 rounded-lg" />
								))}
							</div>
						) : isError ? (
							<IntegrationStatePanel
								state="error"
								errorReason={getApiErrorMessage(
									error,
									"Couldn't load properties — please try again.",
								)}
								onRetry={() => refetch()}
							/>
						) : discoveryError ? (
							// Fix 6: OAuth unconfigured — non-retryable
							<div className="rounded-lg border p-4">
								<p className="text-sm text-foreground">
									Agency Google connection isn&rsquo;t configured — contact the
									AgileCo team.
								</p>
							</div>
						) : properties.length === 0 ? (
							<div className="space-y-4">
								<div className="rounded-lg border border-dashed border-warm-border-strong bg-warm-bg px-8 py-9 text-center">
									<p className="text-dash-title font-semibold text-ink">
										We can&rsquo;t see your property yet.
									</p>
									<div className="mx-auto mt-2 max-w-[460px] text-dash-subtitle text-ink-2">
										<p>
											Make sure the agency&rsquo;s Google account has been granted
											access to your GA4 property, then re-check.
										</p>
										<div className="mt-2 text-dash-context">{grantAccessLink}</div>
									</div>
									<Button
										onClick={() => refetch()}
										className="mt-5 min-h-11 bg-coral text-primary-foreground hover:bg-coral-hover"
									>
										Re-check
									</Button>
									<p className="mt-3.5 text-dash-context text-ink-3">
										Access usually shows up within a few minutes of granting.
									</p>
								</div>
								<div>
									<Button variant="ghost" size="sm" onClick={() => setStep("intro")}>
										Back
									</Button>
								</div>
							</div>
						) : (
							<>
								<div role="radiogroup" aria-label="GA4 property" className="space-y-2">
									{properties.map((prop: Ga4Property) => (
										<label
											key={prop.property_id}
											className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted"
										>
											<input
												type="radio"
												name="ga4-property"
												value={prop.property_id}
												checked={selectedPropertyId === prop.property_id}
												onChange={() => setSelectedPropertyId(prop.property_id)}
												className="h-4 w-4 accent-coral"
											/>
											<span className="min-w-0 flex-1">
												<span className="block truncate text-sm font-medium text-foreground">
													{prop.property_name || prop.property_id}
												</span>
												<span className="block text-dash-context text-muted-foreground">
													{prop.account_name}
												</span>
											</span>
											<Badge variant="outline">{prop.property_id}</Badge>
										</label>
									))}
								</div>
								<div className="flex items-center justify-between">
									<Button variant="ghost" size="sm" onClick={() => setStep("intro")}>
										Back
									</Button>
									<Button
										onClick={() => setStep("confirm")}
										disabled={!selectedPropertyId}
									>
										Continue
									</Button>
								</div>
							</>
						)}
					</div>
				)}

				{step === "confirm" && selectedProperty && (
					<div className="space-y-4">
						<h2 className="text-dash-title font-semibold text-foreground">
							Confirm connection
						</h2>
						<div className="rounded-lg border p-4">
							<span className="text-dash-label uppercase tracking-wider text-muted-foreground">
								Property
							</span>
							<p className="mt-1 text-sm text-foreground">
								{selectedProperty.property_name || selectedProperty.property_id}
							</p>
							<p className="mt-0.5 text-dash-context text-muted-foreground">
								{selectedProperty.account_name} · {selectedProperty.property_id}
							</p>
						</div>
						<p className="text-dash-context text-muted-foreground">
							GA4 data is live once connected — no backfill needed.
						</p>

						{/* Fix 7: inline retry next to error */}
						{connectError && (
							<div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
								<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
								<p className="flex-1 text-sm text-destructive">{connectError}</p>
								<Button
									variant="ghost"
									size="sm"
									onClick={handleConnect}
									disabled={connecting}
								>
									Retry
								</Button>
							</div>
						)}

						<div className="flex items-center justify-between">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setStep("picker");
									setConnectError(null);
								}}
								disabled={connecting}
							>
								Back
							</Button>
							<Button
								onClick={handleConnect}
								disabled={connecting}
								className="gap-1.5"
							>
								{connecting ? (
									<>
										<Loader2 className="h-4 w-4 animate-spin" />
										{/* Fix 4: phase label */}
										Saving connection…
									</>
								) : (
									"Connect"
								)}
							</Button>
						</div>
					</div>
				)}

				{step === "success" && (
					<div className="space-y-4">
						<div className="rounded-lg border border-[hsl(var(--status-success))]/20 bg-[hsl(var(--status-success-bg))]/10 p-4">
							<p className="text-sm text-foreground">
								Connected. GA4 data is live — you&rsquo;ll see it on the SEO Overview.
							</p>
						</div>
						<div className="flex justify-end">
							<Button asChild>
								<Link to="/dashboard">Back to Dashboard</Link>
							</Button>
						</div>
					</div>
				)}
			</Card>
		</div>
	);
}
